/**
 * Path patterns that trigger external navigation (full page load) when they match
 * the resolved URL and optional `appNames` context — same rules as `getNavigationTarget` / CPNavigateTo
 * for non-`alwaysExternal` paths (combined with `shouldAlwaysNavigateExternally` for meet, etc.).
 *
 * Rules with `alwaysExternal: true` apply in every environment. Other rules apply whenever the path matches.
 * Add entries here to force external navigation for specific paths.
 */

export interface ExternalNavigationContext {
  appName?: string
  /** Deploy environment (`runtimeConfig.public.appEnv`). Used when a rule lists `environments`. */
  appEnv?: string
}

export type ExternalPathMatch =
  | { type: 'exact'; path: string; alwaysExternal?: boolean; appNames?: string[]; environments?: string[] }
  | { type: 'prefix'; path: string; alwaysExternal?: boolean; appNames?: string[]; environments?: string[] }

/**
 * Paths that should open with external: true when the path (and optional app context) matches.
 * - exact: path must equal the resolved path (ignoring trailing slash)
 * - prefix: path must start with the given prefix
 * - alwaysExternal: if true, forces external navigation in all environments
 */
export const EXTERNAL_NAVIGATION_PATHS: ExternalPathMatch[] = [
  // Root / index: app at domain root is a different app
  { type: 'exact', path: '/' },
  { type: 'exact', path: '/index' },
  // Meet: separate Nuxt app — navigating to /meet/* from other shells must do a full page load
  { type: 'prefix', path: '/meet', alwaysExternal: true },
  // ClientPortal course product pages are still served by the legacy standalone courses app (production only).
  // Staging uses the same Nuxt SPA for /courses/products/* so MFA and in-app flows are not broken by a full reload.
  { type: 'prefix', path: '/courses/products', appNames: ['CLIENTPORTAL'], environments: ['production'] },
  // In-app upsell / bundle offer detail: legacy courses app route shape (all environments).
  { type: 'prefix', path: '/courses/upsell-offers', appNames: ['CLIENTPORTAL'] },
]

function normalizeAppName(appName?: string): string {
  return (appName || '').trim().toLowerCase()
}

function matchesAppContext(rule: ExternalPathMatch, context: ExternalNavigationContext): boolean {
  if (!rule.appNames?.length) {
    return true
  }

  const currentAppName = normalizeAppName(context.appName)
  if (!currentAppName) {
    return false
  }

  return rule.appNames.some(appName => normalizeAppName(appName) === currentAppName)
}

function normalizeEnvToken(value?: string): string {
  return (value || '').trim().toLowerCase()
}

/**
 * When a rule lists `environments`, it applies only in those deploy environments.
 * If `context.appEnv` is missing, environment-scoped rules never match (prefer SPA over unknown env).
 */
function matchesRuleEnvironments(rule: ExternalPathMatch, context: ExternalNavigationContext): boolean {
  if (!rule.environments?.length) {
    return true
  }
  const current = normalizeEnvToken(context.appEnv)
  if (!current) {
    return false
  }
  return rule.environments.some(env => normalizeEnvToken(env) === current)
}

/**
 * Returns true if the URL path matches any always-external pattern (all environments).
 */
export function shouldAlwaysNavigateExternally(
  url: string,
  patterns: ExternalPathMatch[] = EXTERNAL_NAVIGATION_PATHS,
  context: ExternalNavigationContext = {}
): boolean {
  return shouldNavigateExternally(
    url,
    patterns.filter(p => p.alwaysExternal),
    context
  )
}

/**
 * Returns true if the URL path matches any external-navigation pattern (same path rules as CPNavigateTo).
 */
export function shouldNavigateExternally(
  url: string,
  patterns: ExternalPathMatch[] = EXTERNAL_NAVIGATION_PATHS,
  context: ExternalNavigationContext = {}
): boolean {
  const pathOnly = url.split('?')[0].split('#')[0] || '/'
  const path = pathOnly.endsWith('/') && pathOnly.length > 1 ? pathOnly.slice(0, -1) : pathOnly
  const pathLower = path.toLowerCase()

  return patterns.some(rule => {
    if (!matchesAppContext(rule, context)) {
      return false
    }
    if (!matchesRuleEnvironments(rule, context)) {
      return false
    }

    const { type, path: p } = rule
    const pLower = p.toLowerCase()
    if (type === 'exact') {
      return path === p || pathLower === pLower
    }
    return path === p || path.startsWith(p + '/') || pathLower.startsWith(pLower + '/')
  })
}

/**
 * Runtime override from `runtimeConfig.public.legacyCoursesHandoff` / `NUXT_PUBLIC_LEGACY_COURSES_HANDOFF`.
 * - `'false'`: never use legacy courses handoff for this path (kill switch).
 * - `'true'`: hand off when the URL matches legacy external-navigation patterns (path + appName + appEnv when rules are environment-scoped).
 * - unset / empty / other: same as default CPNavigateTo path rules — `shouldNavigateExternally(...)`.
 */
export function shouldHandoffToLegacyCoursesUrl(
  url: string,
  context: ExternalNavigationContext = {},
  legacyCoursesHandoffEnv?: string | boolean
): boolean {
  if ((legacyCoursesHandoffEnv ?? '').toString().trim().toLowerCase() === 'false') return false
  return shouldNavigateExternally(url, undefined, context)
}

/** Strip query/hash and trailing slash (except root) for pathname comparisons. */
export function normalizePathnameForExternalRules(pathOrUrl: string): string {
  const pathOnly = pathOrUrl.split('?')[0].split('#')[0] || '/'
  return pathOnly.endsWith('/') && pathOnly.length > 1 ? pathOnly.slice(0, -1) : pathOnly
}

/** True when the path is the account settings app shell (always SPA in-app, not external handoff). */
export function isAccountSettingsShellPath(pathname: string): boolean {
  const path = normalizePathnameForExternalRules(pathname).toLowerCase()
  return path === '/account' || path.startsWith('/account/')
}

/** True when the path is the meet app shell (intra-meet navigation should stay SPA, not full reload). */
export function isMeetShellPath(pathname: string): boolean {
  const path = normalizePathnameForExternalRules(pathname).toLowerCase()
  return path === '/meet' || path.startsWith('/meet/')
}
